import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Schedule as PendingIcon,
  PlayArrow as InProgressIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isPast, isToday, isTomorrow } from 'date-fns';
import useProjectStore from '../../../stores/projectStore';
import useAuthStore from '../../../stores/authStore';

// Status configuration - matching the backend enum values exactly
const COLLABORATOR_STATUS = {
  to_do: { label: 'To Do', icon: PendingIcon, color: 'default' },
  in_progress: { label: 'In Progress', icon: InProgressIcon, color: 'info' },
  in_review: { label: 'In Review', icon: InProgressIcon, color: 'warning' },
  done: { label: 'Done', icon: CheckIcon, color: 'success' },
  cancelled: { label: 'Cancelled', icon: CheckIcon, color: 'error' },
};

const TaskCollaborators = ({
  collaborators = [],
  onAddCollaborator,
  onUpdateCollaborator,
  onRemoveCollaborator,
  isEditing = true,
  currentTaskStatus = 'to_do', // Add current task status prop
}) => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const { currentProject } = useProjectStore();
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [editingCollaborator, setEditingCollaborator] = useState(null);

  // Get available team members (exclude already added collaborators)
  const getAvailableMembers = useCallback(() => {
    if (!currentProject?.team) return [];
    
    const collaboratorIds = collaborators.map(c => 
      (c.user?._id || c.user).toString()
    );
    
    return currentProject.team.filter(member => 
      member.user && !collaboratorIds.includes(member.user._id.toString())
    );
  }, [currentProject, collaborators]);

  // Handle add collaborator
  const handleAddCollaborator = () => {
    if (selectedUser) {
      // Find the full user object from the selected ID
      const member = currentProject?.team?.find(m => m.user?._id === selectedUser);
      const userToAdd = member?.user || selectedUser;
      
      onAddCollaborator({
        user: userToAdd,
        deadline: selectedDeadline,
        status: currentTaskStatus,  // Use the current task status instead of defaulting to 'to_do'
      });
      setSelectedUser(null);
      setSelectedDeadline(null);
      setAddMenuAnchor(null);
    }
  };

  // Handle status update
  const handleStatusUpdate = (collaborator, newStatus) => {
    // Check if current user can update this status
    const canUpdate = (collaborator.user._id || collaborator.user).toString() === user._id.toString();
    if (canUpdate || user.role === 'admin') {
      // Find collaborator by matching user ID if no _id exists
      const collaboratorIdentifier = collaborator._id || 
        collaborators.findIndex(c => (c.user._id || c.user).toString() === (collaborator.user._id || collaborator.user).toString());
      onUpdateCollaborator(collaboratorIdentifier, { status: newStatus });
    }
  };

  // Handle deadline update
  const handleDeadlineUpdate = (collaborator, newDeadline) => {
    const collaboratorIdentifier = collaborator._id || 
      collaborators.findIndex(c => (c.user._id || c.user).toString() === (collaborator.user._id || collaborator.user).toString());
    onUpdateCollaborator(collaboratorIdentifier, { deadline: newDeadline });
    setEditingCollaborator(null);
  };

  // Format deadline display
  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    
    const date = new Date(deadline);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isPast(date)) return `Overdue (${format(date, 'MMM d')})`;
    return format(date, 'MMM d, yyyy');
  };

  // Get collaborator display info
  const getCollaboratorInfo = (collaborator) => {
    let userObj = collaborator.user;
    
    // If user is just an ID string, try to find the full user object
    if (typeof userObj === 'string') {
      const member = currentProject?.team?.find(m => m.user?._id === userObj);
      userObj = member?.user || { _id: userObj };
    }
    
    const firstName = userObj?.firstName || '';
    const lastName = userObj?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Unknown User';
    const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '??';
    
    return { fullName, initials, photo: userObj?.photo, userId: userObj?._id || userObj };
  };

  // Check if deadline is overdue
  const isOverdue = (deadline, status) => {
    return deadline && status !== 'done' && isPast(new Date(deadline));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2 
      }}>
        <Typography variant="subtitle2" color="text.secondary">
          Collaborators ({collaborators.length})
        </Typography>
        
        {isEditing && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            sx={{ textTransform: 'none' }}
          >
            Add Collaborator
          </Button>
        )}
      </Box>

      {/* Collaborators List */}
      {collaborators.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {collaborators.map((collaborator, index) => {
            const { fullName, initials, photo, userId } = getCollaboratorInfo(collaborator);
            // Get status config with fallback for unknown statuses
            const statusConfig = COLLABORATOR_STATUS[collaborator.status] || COLLABORATOR_STATUS['to_do'];
            const StatusIcon = statusConfig.icon;
            const overdue = isOverdue(collaborator.deadline, collaborator.status);
            const canUpdateStatus = userId?.toString() === user._id.toString();
            // Use _id if available, otherwise use index as key
            const collaboratorKey = collaborator._id || `temp-${index}-${userId}`;

            return (
              <Box
                key={collaboratorKey}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: collaborator.status === 'done' 
                    ? alpha(theme.palette.success.main, 0.04)
                    : 'background.paper',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.04),
                  },
                }}
              >
                {/* Avatar */}
                <Avatar
                  src={photo}
                  alt={fullName}
                  sx={{ 
                    width: 36, 
                    height: 36,
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  {initials}
                </Avatar>

                {/* Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {fullName}
                    {userId === user._id && (
                      <Chip label="You" size="small" sx={{ ml: 1, height: 20 }} />
                    )}
                  </Typography>
                  
                  {/* Deadline */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    {editingCollaborator === collaboratorKey ? (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={collaborator.deadline ? new Date(collaborator.deadline) : null}
                          onChange={(date) => handleDeadlineUpdate(collaborator, date)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="standard"
                              sx={{ width: 150 }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    ) : (
                      <Typography
                        variant="caption"
                        color={overdue ? 'error' : 'text.secondary'}
                        onClick={() => isEditing && setEditingCollaborator(collaboratorKey)}
                        sx={{ 
                          cursor: isEditing ? 'pointer' : 'default',
                          '&:hover': isEditing ? { textDecoration: 'underline' } : {},
                        }}
                      >
                        {formatDeadline(collaborator.deadline)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Status */}
                <Tooltip title={canUpdateStatus ? 'Click to update status' : statusConfig.label}>
                  <Chip
                    icon={<StatusIcon sx={{ fontSize: 16 }} />}
                    label={statusConfig.label}
                    size="small"
                    color={statusConfig.color}
                    onClick={() => {
                      if (canUpdateStatus && isEditing) {
                        // Cycle through statuses - using correct enum values matching backend
                        const statuses = ['to_do', 'in_progress', 'done'];
                        const currentIndex = statuses.indexOf(collaborator.status);
                        const nextStatus = statuses[(currentIndex + 1) % statuses.length];
                        handleStatusUpdate(collaborator, nextStatus);
                      }
                    }}
                    sx={{
                      cursor: canUpdateStatus && isEditing ? 'pointer' : 'default',
                      minWidth: 100,
                    }}
                  />
                </Tooltip>

                {/* Remove button */}
                {isEditing && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const collaboratorIdentifier = collaborator._id || 
                        collaborators.findIndex(c => (c.user._id || c.user).toString() === (collaborator.user._id || collaborator.user).toString());
                      onRemoveCollaborator(collaboratorIdentifier);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            py: 3,
            textAlign: 'center',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.action.hover, 0.02),
          }}
        >
          <PersonIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No collaborators added
          </Typography>
          {isEditing && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Add First Collaborator
            </Button>
          )}
        </Box>
      )}

      {/* Add Collaborator Menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => {
          setAddMenuAnchor(null);
          setSelectedUser(null);
          setSelectedDeadline(null);
        }}
        PaperProps={{
          sx: { width: 320, p: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Add Collaborator
        </Typography>
        
        {/* User Selection */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Select Team Member
        </Typography>
        <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
          {getAvailableMembers().map((member) => (
            <MenuItem
              key={member.user._id}
              selected={selectedUser === member.user._id}
              onClick={() => setSelectedUser(member.user._id)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <Avatar
                src={member.user.photo}
                sx={{ width: 24, height: 24, mr: 1 }}
              >
                {member.user.firstName?.[0]}
              </Avatar>
              {member.user.firstName} {member.user.lastName}
            </MenuItem>
          ))}
        </Box>

        {/* Deadline Selection */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Set Deadline (Optional)
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={selectedDeadline}
            onChange={setSelectedDeadline}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
          />
        </LocalizationProvider>

        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() => {
              setAddMenuAnchor(null);
              setSelectedUser(null);
              setSelectedDeadline(null);
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!selectedUser}
            onClick={handleAddCollaborator}
          >
            Add
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default TaskCollaborators;